import {
  Component,
  OnChanges,
  Input,
  SimpleChanges,
  inject,
} from '@angular/core';
import { DashboardService } from '../../dashboard.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { DateHandlingService } from '../../../services/date-handling.service';
import Highcharts from 'highcharts';

@Component({
  selector: 'app-summary-charts',
  templateUrl: './summaryCharts.component.html',
  styleUrls: ['./summaryCharts.component.scss'],
  standalone: false,
})
export class SummaryChartsComponent implements OnChanges {
  private dbs = inject(DashboardService);
  private dateService = inject(DateHandlingService);
  private ss = inject(SessionStorageService);

  @Input() solutionId;
  @Input() start;
  @Input() end;
  @Input() selectedUnit;

  Highcharts: typeof Highcharts = Highcharts;
  loading = false;
  caseData = {};
  chartData = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.solutionId && changes.solutionId.previousValue !== undefined) ||
      (changes.end && changes.end.previousValue === null) ||
      changes.selectedUnit
    ) {
      this.getCaseStats();
    }
  }

  getCaseStats() {
    this.loading = true;

    this.dbs
      .getActivitySummary(
        this.selectedUnit,
        this.dateService.toUseableIso(this.start, 'start'),
        this.dateService.toUseableIso(this.end, 'end')
      )
      .subscribe(resp => {
        const data = resp.Summary.find(
          summary =>
            summary.Solution.toLowerCase() === this.solutionId.toLowerCase()
        );
        this.buildChartsArray(data);
        this.loading = false;
      });
  }

  buildChartsArray(data) {
    const total =
      (data?.ActiveCases || 0) +
      (data?.Executions || 0) +
      (data?.ScoreClassChanges || 0);
    this.chartData = [];

    this.chartData.push(
      this.buildChart({
        data: data?.ActiveCases || 0,
        label: 'Cases With Interactions',
        total: total,
        color: 2,
      })
    );
    this.chartData.push(
      this.buildChart({
        data: data?.Executions || 0,
        label: 'Case Run Count',
        total: total,
        color: 3,
      })
    );
    this.chartData.push(
      this.buildChart({
        data: data?.ScoreClassChanges || 0,
        label: 'Score Classes Changed',
        total: total,
        color: 4,
      })
    );
  }

  buildChart(data) {
    const colors = ['#ffcb21', '#f77c00', '#89c540', '#6734ba', '#CD0000'];
    return {
      chart: {
        type: 'solidgauge',
        // Edit chart spacing
        spacingBottom: 0,
        spacingTop: 0,
        spacingLeft: 0,
        spacingRight: 0,
        // Explicitly tell the width and height of a chart
        // width: '100%',
        // height: '100%',
        backgroundColor: null,
      },

      title: {
        text: null,
      },

      exporting: {
        enabled: false,
      },

      credits: {
        enabled: false,
      },

      tooltip: {
        enabled: false,
      },

      pane: {
        size: '100%',
        startAngle: 0,
        endAngle: 360,
        background: [
          {
            // Track for Move
            outerRadius: '100%',
            innerRadius: '85%',
            backgroundColor: '#e6e7e8',
            borderWidth: 0,
          },
        ],
      },

      yAxis: {
        min: 0,
        max: data.total,
        lineWidth: 0,
        tickPositions: [],
      },

      plotOptions: {
        solidgauge: {
          dataLabels: {
            enabled: true,
            borderWidth: 0,
            // format: '<span style="text-align: center; width: 100%; display: inline-block;">{point.y}<br>{series.name}</span>',
            padding: 0,
            x: 0,
            y: 0,
            verticalAlign: 'middle',
            align: 'center',
            style: {
              width: '75%',
              //  fontSize: '18px',
              fontFamily: 'Roboto',
              textOverflow: 'clip',
            },
            useHTML: true,
            formatter: function () {
              return `<span style="text-align: center; width: 100%; font-size: 26px; display: inline-block; color: ${
                colors[data.color]
              };">${
                this.point.y
              }<br><span style="text-align: center; width: 100%; font-size: 14px; display: inline-block; font-weight: 400; color: #939597;">${
                this.series.name
              }</span></span>`;
            },
            shadow: false,
          },

          linecap: 'round',
          stickyTracking: false,
          rounded: true,
        },
      },

      series: [
        {
          name: data.label,
          data: [
            {
              color: colors[data.color],
              radius: '100%',
              innerRadius: '85%',
              y: data.data,
            },
          ],
        },
      ],
    };
  }
}
